import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography, Chip, Button, CircularProgress } from "@mui/material";
import axiosInstant from "../../../../api";
import { getUserDocuments, getUserSigningUrl, downloadUserDocument, sendUserMsa } from "../../../../api/endpoints";
import { useBoundStore } from "../../../../store";

interface Document {
    envelopeId: string;
    type: string;
    name: string;
    status: string;
    sentDateTime: string | null;
    completedDateTime: string | null;
}

const statusColors: Record<string, "success" | "warning" | "error" | "default"> = {
    completed: "success",
    sent: "warning",
    delivered: "warning",
    declined: "error",
    voided: "error"
};

export default function FormDocuments({
    userId,
    msaAgreementSigned,
    expertName
}: {
    userId: number;
    msaAgreementSigned: boolean;
    expertName: string;
}) {
    const user = useBoundStore((state) => state.user);
    const setUserInfo = useBoundStore((state) => state.setUserInfo);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState("");
    const [signingActive, setSigningActive] = useState(false);
    const [signingLoading, setSigningLoading] = useState(false);
    const [signingComplete, setSigningComplete] = useState(false);
    const signingContainerRef = useRef<HTMLDivElement>(null);

    const isOwnProfile = user?.userid === userId && user?.usertype === "verified creator";

    const fetchDocuments = useCallback(async () => {
        if (!userId) return;
        try {
            setLoading(true);
            const response = await axiosInstant.get(getUserDocuments(userId));
            const docs = response.data.documents || [];
            setDocuments(docs);
            // If DocuSign confirms the envelope is complete, sync the store so
            // the banner hides and the confirmation guard passes — covers webhook
            // delay and cases where signing_complete never fired (e.g. JS URL mismatch)
            if (docs.some((d: Document) => d.type === "msa" && d.status === "completed")) {
                const currentUser = useBoundStore.getState().user;
                if (currentUser && !currentUser.msaAgreementSigned) {
                    setUserInfo({ ...currentUser, msaAgreementSigned: true });
                }
            }
        } catch (err: any) {
            console.error("Failed to fetch documents:", err);
            setError("Failed to load documents");
        } finally {
            setLoading(false);
        }
    }, [userId, setUserInfo]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleSendMsa = useCallback(async () => {
        try {
            setSending(true);
            setSendError("");
            const response = await axiosInstant.post(sendUserMsa(userId));
            setDocuments([response.data.document]);
        } catch (err: any) {
            setSendError(err?.response?.data?.error || "Failed to send MSA. Please try again.");
        } finally {
            setSending(false);
        }
    }, [userId]);

    // Auto-send MSA for legacy experts who have no envelope on file
    useEffect(() => {
        if (!loading && documents.length === 0 && isOwnProfile && !msaAgreementSigned && !sending && !sendError) {
            handleSendMsa();
        }
    }, [loading, documents.length, isOwnProfile, user?.msaAgreementSigned, sending, sendError, handleSendMsa]);

    const handleSign = async () => {
        try {
            setSigningActive(true);
            setSigningLoading(true);
            setError("");

            // Get the signing URL and integration key from the backend
            const response = await axiosInstant.get(getUserSigningUrl(userId));
            const { url, integrationKey, jsUrl } = response.data;

            // Load DocuSign JS library if not already loaded
            if (!(window as any).DocuSign) {
                await new Promise<void>((resolve, reject) => {
                    const script = document.createElement("script");
                    script.src = jsUrl;
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error("Failed to load DocuSign JS"));
                    document.head.appendChild(script);
                });
            }

            const docusign = await (window as any).DocuSign.loadDocuSign(integrationKey);

            const signing = docusign.signing({
                url,
                displayFormat: "default",
                style: {
                    branding: {
                        primaryButton: {
                            backgroundColor: "#333",
                            color: "#fff"
                        }
                    },
                    signingNavigationButton: {
                        finishText: "Complete Signing",
                        position: "bottom-center"
                    }
                }
            });

            signing.on("ready", () => {
                setSigningLoading(false);
            });

            signing.on("sessionEnd", (event: any) => {
                console.log("[DocuSign] Session ended:", event);
                setSigningActive(false);

                if (event.sessionEndType === "signing_complete") {
                    setSigningComplete(true);
                    fetchDocuments();
                    // Update store directly so the banner hides immediately
                    if (user) {
                        setUserInfo({ ...user, msaAgreementSigned: true });
                    }
                }
            });

            if (signingContainerRef.current) {
                signing.mount(signingContainerRef.current);
            }
        } catch (err: any) {
            console.error("Failed to start signing:", err);
            setError(err?.response?.data?.error || "Failed to start signing session");
            setSigningActive(false);
        }
    };

    const handleDownload = async (doc: Document) => {
        try {
            const response = await axiosInstant.get(downloadUserDocument(userId, doc.envelopeId), {
                responseType: "blob"
            });
            const blob = new Blob([response.data], { type: "application/pdf" });
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, "_blank");
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
        } catch (err) {
            console.error("Failed to download document:", err);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !signingActive) {
        return (
            <Typography color="error" py={2}>
                {error}
            </Typography>
        );
    }

    // Show signing complete message
    if (signingComplete) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
                <Typography variant="h6" color="success.main">
                    MSA signed successfully!
                </Typography>
                <Typography color="text.secondary">Your signed Master Service Agreement is now on file.</Typography>
                {documents.length > 0 && documents[0].envelopeId && (
                    <Button variant="outlined" onClick={() => handleDownload(documents[0])}>
                        Download Signed MSA
                    </Button>
                )}
            </Box>
        );
    }

    // Show embedded signing view
    if (signingActive) {
        return (
            <Box>
                {signingLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                    </Box>
                )}
                <div ref={signingContainerRef} style={{ width: "100%", height: "800px" }} />
            </Box>
        );
    }

    // Auto-sending MSA for legacy expert
    if (sending) {
        return (
            <Box display="flex" alignItems="center" gap={2} py={4}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Sending your MSA...</Typography>
            </Box>
        );
    }

    // Auto-send failed — show retry
    if (sendError) {
        return (
            <Box display="flex" flexDirection="column" gap={2} py={2}>
                <Typography color="error">{sendError}</Typography>
                <Button variant="outlined" onClick={handleSendMsa} sx={{ alignSelf: "flex-start" }}>
                    Try Again
                </Button>
            </Box>
        );
    }

    // Expert who signed outside the portal
    if (documents.length === 0 && msaAgreementSigned) {
        return (
            <Typography color="text.secondary" py={2}>
                {isOwnProfile
                    ? "You have already signed your MSA outside the portal. No further action is needed."
                    : `${expertName} has already signed their MSA outside the portal.`}
            </Typography>
        );
    }

    if (documents.length === 0) {
        return (
            <Typography color="text.secondary" py={2}>
                No MSA has been sent yet.
            </Typography>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {documents.map((doc) => (
                <Box
                    key={doc.envelopeId}
                    sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                        flexDirection: { xs: "column", sm: "row" },
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2
                    }}
                >
                    <Box display="flex" flexDirection="column" gap={0.5} sx={{ minWidth: 0, width: "100%" }}>
                        <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                            {doc.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {doc.type.toUpperCase()}
                            {doc.sentDateTime && ` \u2022 Sent ${new Date(doc.sentDateTime).toLocaleDateString()}`}
                            {doc.completedDateTime &&
                                ` \u2022 Signed ${new Date(doc.completedDateTime).toLocaleDateString()}`}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1.5, sm: 2 },
                            flexWrap: "wrap",
                            justifyContent: { xs: "flex-end", sm: "flex-start" },
                            width: { xs: "100%", sm: "auto" }
                        }}
                    >
                        <Chip
                            label={doc.status}
                            variant="outlined"
                            color={statusColors[doc.status] || "default"}
                            size="small"
                        />
                        {doc.status === "completed" ? (
                            <Button variant="outlined" size="small" onClick={() => handleDownload(doc)}>
                                Download
                            </Button>
                        ) : isOwnProfile ? (
                            <Button variant="contained" size="small" onClick={handleSign}>
                                Sign Now
                            </Button>
                        ) : null}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
